import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, ChevronRight, Plus, Trash2, Clock, FileText,
  Package, AlertCircle, Loader2, ShieldCheck, CheckCircle2,
  AlertTriangle, XCircle, BarChart3, Mail, Flag, Calendar, Filter, Users,
  GitBranch, MessageSquareText, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RecommendationBadge } from '../components/Badges';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STAT_CARDS = [
  {
    key: 'ALL',
    label: 'Total Audits',
    valueKey: 'total_audits',
    subKey: 'total_skus',
    subLabel: 'SKUs analysed',
    icon: ShieldCheck,
    iconColor: 'text-primary',
    numColor: 'text-foreground',
    activeClass: 'active',
  },
  {
    key: 'ALLOW',
    label: 'ALLOW',
    valueKey: 'total_allow',
    subLabel: 'decisions',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600',
    numColor: 'text-emerald-700',
    activeClass: 'active-allow',
  },
  {
    key: 'REVIEW',
    label: 'ALERT',
    valueKey: 'total_review',
    subLabel: 'items',
    icon: AlertTriangle,
    iconColor: 'text-orange-500',
    numColor: 'text-orange-700',
    activeClass: 'active-review',
  },
  {
    key: 'REJECT',
    label: 'HIGH ALERT',
    valueKey: 'total_reject',
    subLabel: 'items',
    icon: XCircle,
    iconColor: 'text-red-500',
    numColor: 'text-red-700',
    activeClass: 'active-reject',
  },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function getActionStatusBadge(actionPlan) {
  if (!actionPlan || !actionPlan.status) return null;
  
  const STATUS_CONFIG = {
    pending: {
      label: 'Pending',
      icon: Clock,
      className: 'bg-gray-100 text-gray-700 border-gray-300',
    },
    awaiting_response: {
      label: 'Awaiting',
      icon: Mail,
      className: 'bg-blue-100 text-blue-700 border-blue-300',
    },
    resolved: {
      label: 'Resolved',
      icon: CheckCircle2,
      className: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    },
    escalated: {
      label: 'Escalated',
      icon: AlertTriangle,
      className: 'bg-red-100 text-red-700 border-red-300',
    },
    no_action_needed: {
      label: 'No Action',
      icon: CheckCircle2,
      className: 'bg-gray-100 text-gray-600 border-gray-300',
    },
  };
  
  const config = STATUS_CONFIG[actionPlan.status];
  if (!config) return null;
  
  // Calculate deadline info
  let deadlineInfo = null;
  if (actionPlan.follow_up_deadline && actionPlan.status === 'awaiting_response') {
    const deadline = new Date(actionPlan.follow_up_deadline);
    const now = new Date();
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    const isOverdue = daysLeft < 0;
    const isUrgent = daysLeft <= 2 && daysLeft >= 0;
    deadlineInfo = { daysLeft, isOverdue, isUrgent };
  }
  
  return { config, deadlineInfo };
}

function getBrandSummary(lineItems = []) {
  /**
   * Extract brand summary from line items
   * Returns: { brands: ['SAMSUNG', 'APPLE'], brandCounts: { SAMSUNG: 50, APPLE: 10 } }
   */
  const brands = ['SAMSUNG', 'APPLE', 'VIVO', 'OPPO', 'NOKIA', 'HUAWEI', 'REALME', 'XIAOMI', 'MOTOROLA', 'ITEL', 'TECNO', 'INFINIX'];
  const brandCounts = {};
  
  lineItems.forEach(item => {
    const model = (item.model || '').toUpperCase();
    let found = false;
    
    for (const brand of brands) {
      if (model.includes(brand)) {
        brandCounts[brand] = (brandCounts[brand] || 0) + 1;
        found = true;
        break;
      }
    }
    
    if (!found) {
      brandCounts['Other'] = (brandCounts['Other'] || 0) + 1;
    }
  });
  
  // Sort by count descending
  const sortedBrands = Object.entries(brandCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([brand, count]) => ({ brand, count }));
  
  return sortedBrands;
}

// Progress-summary status → visual config (used in audit cards)
const PROGRESS_STATUS_CONFIG = {
  on_track:    { label: 'On Track',    className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  at_risk:     { label: 'At Risk',     className: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:    { label: 'Resolved',    className: 'bg-blue-50 text-blue-700 border-blue-200' },
  escalated:   { label: 'Escalated',   className: 'bg-red-50 text-red-700 border-red-200' },
  no_response: { label: 'No reply yet', className: 'bg-slate-50 text-slate-600 border-slate-200' },
};

// ── Audit Card ──────────────────────────────────────────────────────────
function AuditCard({ audit, idx = 0, deleting, onDelete, onClick, compact = false }) {
  const summary    = audit.recommendation_summary || {};
  const lineItems  = audit.analysis?.line_items || [];
  const isGmail    = audit.source === 'gmail' || !!audit.gmail_thread_id;
  const threadSubject = audit.thread_subject || audit.gmail_source?.subject;
  const progressStatus = audit.progress_summary?.overall_status;
  const progressCfg = PROGRESS_STATUS_CONFIG[progressStatus];
  const messageCount = audit.thread?.message_count;
  const weekNumber = audit.week_number;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(idx * 0.03, 0.3) }}
    >
      <Card
        className="cursor-pointer hover:shadow-md transition-all duration-150 border-border/70 group active:scale-[0.995]"
        onClick={onClick}
        data-testid={`history-audit-item-${audit._id}`}
      >
        <CardContent className={compact ? 'p-3' : 'p-4'}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Title — thread subject (Gmail) or country */}
              {threadSubject && (
                <div className="flex items-center gap-2 mb-1.5">
                  {weekNumber != null && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">
                      W{weekNumber}
                    </span>
                  )}
                  <p className="text-sm font-semibold text-foreground truncate" title={threadSubject}>
                    {threadSubject}
                  </p>
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />{formatDate(audit.created_at)}
                </span>
                {audit.files?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {audit.files.length} file{audit.files.length > 1 ? 's' : ''}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  {summary.total || lineItems.length} SKU{(summary.total || lineItems.length) !== 1 ? 's' : ''}
                </span>

                {/* Audited (Gmail) badge */}
                {isGmail && (
                  <span
                    data-testid="audit-source-badge-gmail"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200"
                  >
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    AUDITED · GMAIL
                    {messageCount > 1 && (
                      <span className="ml-0.5 opacity-70">· {messageCount} msgs</span>
                    )}
                  </span>
                )}

                {/* Progress summary status */}
                {progressCfg && (
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${progressCfg.className}`}>
                    <MessageSquareText className="w-2.5 h-2.5" />
                    {progressCfg.label}
                  </span>
                )}

                {/* Action Status Badge */}
                {(() => {
                  const statusInfo = getActionStatusBadge(audit.action_plan);
                  if (!statusInfo) return null;
                  const { config, deadlineInfo } = statusInfo;
                  const Icon = config.icon;
                  return (
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${config.className}`}>
                      <Icon className="w-2.5 h-2.5" />
                      {config.label}
                      {deadlineInfo && deadlineInfo.isOverdue && (
                        <span className="ml-0.5 text-red-700">({Math.abs(deadlineInfo.daysLeft)}d overdue)</span>
                      )}
                      {deadlineInfo && deadlineInfo.isUrgent && (
                        <span className="ml-0.5 text-amber-700">({deadlineInfo.daysLeft}d left)</span>
                      )}
                    </span>
                  );
                })()}

                {/* Team Member Badge */}
                {audit.team_member?.name && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-300 text-[10px] font-medium">
                    {audit.team_member.name}
                  </span>
                )}
              </div>

              {/* Progress headline */}
              {audit.progress_summary?.headline && !compact && (
                <p className="text-xs text-slate-700 italic mt-1.5 truncate max-w-xl">
                  “{audit.progress_summary.headline}”
                </p>
              )}

              {/* Email preview */}
              {audit.email_body_preview && !compact && (
                <p className="text-xs text-muted-foreground mt-1.5 truncate max-w-xl">
                  {audit.email_body_preview}
                </p>
              )}

              {/* SKU chips */}
              {lineItems.length > 0 && !compact && (
                <>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {lineItems.slice(0, 4).map(item => (
                      <span key={item.model}
                        className="font-mono text-[11px] px-1.5 py-0.5 bg-secondary rounded border border-border/60 truncate max-w-[180px]">
                        {item.model}
                      </span>
                    ))}
                    {lineItems.length > 4 && (
                      <span className="text-xs text-muted-foreground self-center">+{lineItems.length - 4} more</span>
                    )}
                  </div>

                  {(() => {
                    const brands = getBrandSummary(lineItems);
                    if (brands.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border/50">
                        <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">
                          Brands:
                        </span>
                        {brands.slice(0, 3).map(({ brand, count }) => (
                          <span key={brand}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">
                            {brand} ({count})
                          </span>
                        ))}
                        {brands.length > 3 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{brands.length - 3} more
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex flex-col gap-1 items-end">
                {summary.allow  > 0 && <RecommendationBadge tier="ALLOW" />}
                {summary.review > 0 && <RecommendationBadge tier="ALERT" />}
                {summary.reject > 0 && <RecommendationBadge tier="HIGH_ALERT" />}
              </div>
              <div className="sm:hidden">
                {summary.reject > 0 ? <RecommendationBadge tier="HIGH_ALERT" /> :
                 summary.review > 0 ? <RecommendationBadge tier="ALERT" /> :
                 <RecommendationBadge tier="ALLOW" />}
              </div>
              {onDelete && (
                <Button
                  variant="ghost" size="sm"
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={e => { e.stopPropagation(); onDelete(audit._id); }}
                  disabled={deleting === audit._id}
                  data-testid={`delete-audit-${audit._id}`}
                >
                  {deleting === audit._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              )}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function History() {
  const navigate = useNavigate();
  const [audits, setAudits]       = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [activeCard, setActiveCard] = useState('ALL');  // drilldown filter
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleting, setDeleting]   = useState(null);
  const [actionFilter, setActionFilter] = useState('all');  // NEW: action status filter
  const [groupBy, setGroupBy]     = useState('series');  // 'series' (thread series), 'team_member', 'none'
  const [series, setSeries]       = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // Fetch aggregate stats
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch(`${BACKEND}/api/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStats(d); })
      .finally(() => setStatsLoading(false));
  }, [audits]);

  const fetchAudits = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (activeCard !== 'ALL') params.append('recommendation', activeCard);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${BACKEND}/api/audits?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAudits(data.audits);
      setTotalPages(data.pages);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [page, activeCard]);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  // Fetch series view (thread groupings) when groupBy === 'series'
  useEffect(() => {
    if (groupBy !== 'series') return;
    let cancelled = false;
    setSeriesLoading(true);
    fetch(`${BACKEND}/api/audits/series`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { series: [] })
      .then(d => { if (!cancelled) setSeries(d.series || []); })
      .catch(() => { if (!cancelled) setSeries([]); })
      .finally(() => { if (!cancelled) setSeriesLoading(false); });
    return () => { cancelled = true; };
  }, [groupBy, audits]);

  // When card changes, reset to page 1
  const handleCardClick = (key) => {
    setActiveCard(key);
    setPage(1);
    setSearch('');
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      const res = await fetch(`${BACKEND}/api/audits/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Audit deleted');
      setAudits(prev => prev.filter(a => a._id !== id));
    } catch (err) { toast.error(err.message); }
    finally { setDeleting(null); }
  };

  // Client-side filtering (search + action status)
  const filtered = audits.filter(a => {
    // Search filter
    if (search) {
      const q = search.toLowerCase();
      const matchesSearch = (
        a.email_body_preview?.toLowerCase().includes(q) ||
        a.analysis?.line_items?.some(i => i.model?.toLowerCase().includes(q)) ||
        a.team_member?.email?.toLowerCase().includes(q) ||
        a.team_member?.name?.toLowerCase().includes(q)
      );
      if (!matchesSearch) return false;
    }
    
    // Action status filter
    if (actionFilter !== 'all') {
      const status = a.action_plan?.status;
      if (actionFilter === 'with_actions' && !status) return false;
      if (actionFilter === 'no_actions' && status) return false;
      if (actionFilter !== 'with_actions' && actionFilter !== 'no_actions' && status !== actionFilter) return false;
    }
    
    return true;
  });

  // Group audits by team member if requested
  const displayData = groupBy === 'team_member'
    ? Object.entries(
        filtered.reduce((acc, audit) => {
          const email = audit.team_member?.email || 'unknown';
          if (!acc[email]) acc[email] = [];
          acc[email].push(audit);
          return acc;
        }, {})
      )
        .filter(([email]) => email !== 'unknown')  // Filter out Unknown when grouped
        .sort((a, b) => b[1].length - a[1].length)
    : [['all', filtered]];

  return (
    <div className="page-pad max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Audit History
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">All past supply chain audits</p>
        </div>
        <Button onClick={() => navigate('/new-audit')} className="gap-2 h-9">
          <Plus className="w-4 h-4" />New Audit
        </Button>
      </div>

      {/* ─ DRILLDOWN STAT CARDS ─ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {STAT_CARDS.map(({ key, label, valueKey, subKey, subLabel, icon: Icon, iconColor, numColor, activeClass }) => {
          const val  = statsLoading ? null : (stats?.[valueKey] ?? 0);
          const sub  = subKey ? (stats?.[subKey] ?? 0) : null;
          const isActive = activeCard === key;
          return (
            <motion.div
              key={key}
              whileTap={{ scale: 0.97 }}
              className={`stat-card ${isActive ? activeClass : ''}`}
              onClick={() => handleCardClick(key)}
              data-testid={`stat-card-${key.toLowerCase()}`}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleCardClick(key)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                  {statsLoading ? (
                    <div className="h-8 w-16 bg-secondary rounded animate-pulse" />
                  ) : (
                    <p className={`text-3xl font-bold tabular-nums leading-none ${numColor}`}
                      style={{ fontFamily: 'Space Grotesk' }}>
                      {(val ?? 0).toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {sub !== null ? `${sub.toLocaleString()} ${subLabel}` : subLabel}
                  </p>
                </div>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isActive ? 'bg-white/70' : 'bg-secondary'
                }`}>
                  <Icon className={`w-4.5 h-4.5 ${iconColor}`} style={{ width: 18, height: 18 }} />
                </div>
              </div>
              {isActive && (
                <motion.div
                  layoutId="card-indicator"
                  className="mt-2 h-0.5 rounded-full bg-current opacity-30"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Active filter banner */}
      <AnimatePresence>
        {activeCard !== 'ALL' && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-accent border border-primary/20 text-xs"
          >
            <BarChart3 className="w-3.5 h-3.5 text-primary" />
            <span className="text-primary font-medium">Filtered by: <strong>{activeCard}</strong></span>
            <button onClick={() => handleCardClick('ALL')}
              className="ml-auto text-muted-foreground hover:text-foreground transition-colors">
              Clear ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="history-search-input"
            placeholder="Search by SKU or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* Action Status Filter */}
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[180px] h-9">
            <Filter className="w-3.5 h-3.5 mr-2" />
            <SelectValue placeholder="Action Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Audits</SelectItem>
            <SelectItem value="with_actions">With Actions</SelectItem>
            <SelectItem value="no_actions">No Actions</SelectItem>
            <SelectItem value="awaiting_response">Awaiting</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
          </SelectContent>
        </Select>

        {/* Group By Toggle */}
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-[200px] h-9" data-testid="history-groupby-select">
            <Users className="w-3.5 h-3.5 mr-2" />
            <SelectValue placeholder="Group By" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="series">By Thread Series (Country · Brand)</SelectItem>
            <SelectItem value="team_member">By Team Member</SelectItem>
            <SelectItem value="none">No Grouping</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Grouping Info Banner */}
      {groupBy === 'series' && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
          <GitBranch className="w-3.5 h-3.5 text-emerald-700" />
          <span className="text-emerald-800 font-medium">
            Grouped by thread series — multi-week reorders for the same country + brand are linked.
          </span>
        </div>
      )}
      {groupBy === 'team_member' && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 text-xs">
          <Users className="w-3.5 h-3.5 text-primary" />
          <span className="text-primary font-medium">
            Grouped by team member · Audits without team data are hidden
          </span>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <motion.div
          data-testid="history-empty-state"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            {activeCard !== 'ALL' ? `No ${activeCard} audits` : search ? 'No matching audits' : 'No audits yet'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {activeCard !== 'ALL'
              ? `No audits with ${activeCard} decisions found.`
              : search ? 'Try adjusting your search.' : 'Run your first supply chain audit.'}
          </p>
          <Button onClick={() => navigate('/new-audit')} className="gap-2">
            <Plus className="w-4 h-4" />Run Audit
          </Button>
        </motion.div>
      )}

      {/* List */}
      {!loading && groupBy === 'series' && (
        <motion.div
          data-testid="history-series-list"
          className="space-y-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {seriesLoading && (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          )}

          {!seriesLoading && series.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <GitBranch className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                No thread series yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Audit reorder emails from Gmail and they'll appear here grouped by country + brand
                (e.g. Ghana · Vivo) so you can track Week-over-Week progression.
              </p>
              <Button onClick={() => navigate('/emails')} className="gap-2">
                <Mail className="w-4 h-4" />Browse Gmail
              </Button>
            </div>
          )}

          {!seriesLoading && series.map(s => (
            <div key={s.series_key} data-testid={`series-${s.series_key}`}>
              {/* Series header */}
              <div className="bg-gradient-to-r from-emerald-50 via-white to-transparent border border-emerald-200 rounded-xl p-3.5 mb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <GitBranch className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground">
                        {s.display_name}
                      </h3>
                      <p className="text-[11px] text-muted-foreground">
                        {s.audit_count} audit{s.audit_count !== 1 ? 's' : ''}
                        {s.latest_week != null && ` · latest W${s.latest_week}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="h-5 text-xs">
                      {s.audit_count} week{s.audit_count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </div>
                {/* Week timeline strip */}
                {s.audits.some(a => a.week_number != null) && (
                  <div className="flex flex-wrap gap-1 mt-2.5">
                    {s.audits
                      .filter(a => a.week_number != null)
                      .map(a => {
                        const status = a.progress_summary?.overall_status;
                        const cfg = PROGRESS_STATUS_CONFIG[status] || PROGRESS_STATUS_CONFIG.no_response;
                        return (
                          <button
                            key={a._id}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/results/${a._id}`); }}
                            className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider transition hover:scale-105 ${cfg.className}`}
                            title={a.thread_subject || ''}
                            data-testid={`series-week-chip-${a.week_number}`}
                          >
                            W{a.week_number}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Audits within series */}
              <div className="space-y-2.5">
                {s.audits.map((audit, idx) => (
                  <AuditCard
                    key={audit._id}
                    audit={audit}
                    idx={idx}
                    deleting={deleting}
                    onDelete={(id) => handleDelete({ stopPropagation: () => {} }, id)}
                    onClick={() => navigate(`/results/${audit._id}`)}
                    compact
                  />
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {!loading && groupBy !== 'series' && filtered.length > 0 && (
        <motion.div
          data-testid="history-audit-list"
          className="space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {displayData.map(([groupKey, groupAudits]) => (
            <div key={groupKey}>
              {/* Group Header (if grouped by team member) */}
              {groupBy === 'team_member' && groupKey !== 'all' && (
                <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/20 rounded-xl p-3.5 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-foreground">
                          {groupAudits[0]?.team_member?.name || 'Unknown'}
                        </h3>
                        <p className="text-[11px] text-muted-foreground">
                          {groupKey !== 'unknown' ? groupKey : 'no email'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="h-5 text-xs">
                        {groupAudits.length} audit{groupAudits.length !== 1 ? 's' : ''}
                      </Badge>
                      
                      {(() => {
                        const totalSkus = groupAudits.reduce((sum, a) => 
                          sum + (a.recommendation_summary?.total || 0), 0
                        );
                        const totalRejects = groupAudits.reduce((sum, a) => 
                          sum + (a.recommendation_summary?.reject || 0), 0
                        );
                        return (
                          <>
                            <Badge variant="outline" className="h-5 text-xs">
                              {totalSkus} SKUs
                            </Badge>
                            {totalRejects > 0 && (
                              <Badge variant="destructive" className="h-5 text-xs">
                                {totalRejects} HIGH ALERT
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Audit Cards */}
              <div className="space-y-2.5">
                {groupAudits.map((audit, idx) => (
                  <AuditCard
                    key={audit._id}
                    audit={audit}
                    idx={idx}
                    deleting={deleting}
                    onDelete={(id) => handleDelete({ stopPropagation: () => {} }, id)}
                    onClick={() => navigate(`/results/${audit._id}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Pagination (hidden in series view) */}
      {groupBy !== 'series' && totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="flex items-center text-sm text-muted-foreground px-2">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
